import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import FileUploader from "@/components/FileUploader";
import { Save, Building2, Landmark, PenTool } from "lucide-react";

const FIELDS = [
  { g: "company", label: "Company", icon: Building2, items: [
    ["company_name", "Legal Name"],
    ["company_gstin", "GSTIN"],
    ["company_pan", "PAN"],
    ["company_state", "State"],
    ["company_state_code", "State Code"],
    ["company_phone", "Phone"],
    ["company_email", "Email"],
    ["company_website", "Website"],
  ]},
  { g: "bank", label: "Bank", icon: Landmark, items: [
    ["bank_name", "Bank Name"],
    ["bank_account", "Account Number"],
    ["bank_ifsc", "IFSC"],
  ]},
  { g: "sig", label: "Signatory", icon: PenTool, items: [
    ["signatory_name", "Authorized Signatory Name"],
  ]},
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings").then(r => setS(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { company_name, company_address, company_gstin, company_pan, company_state, company_state_code, company_phone, company_email, company_website, bank_name, bank_account, bank_ifsc, signatory_name } = s;
      await api.put("/settings", { company_name, company_address, company_gstin, company_pan, company_state, company_state_code, company_phone, company_email, company_website, bank_name, bank_account, bank_ifsc, signatory_name });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const setFile = async (key, path) => {
    const next = { ...s, [key]: path };
    setS(next);
    await api.patch("/settings/files", { [key]: path });
    toast.success(path ? "File updated" : "File cleared");
  };

  if (!s) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>Settings</h1>
          <p className="mt-2 text-sm text-slate-500">Company details, bank & signatory used across quotations and invoices.</p>
        </div>
        <Button onClick={save} disabled={busy} data-testid="settings-save" className="bg-[#0F2942] text-white hover:bg-[#0A192F]">
          <Save className="mr-2 h-4 w-4" />{busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="mt-8 space-y-6">
        {FIELDS.map(({ g, label, icon: Icon, items }) => (
          <Card key={g} className="p-6">
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
              <Icon className="h-4 w-4" /> {label}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {items.map(([k, l]) => (
                <div key={k}>
                  <Label>{l}</Label>
                  <Input data-testid={`settings-${k}`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} />
                </div>
              ))}
              {g === "company" && (
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Textarea rows={2} data-testid="settings-company_address" value={s.company_address || ""} onChange={(e) => setS({ ...s, company_address: e.target.value })} />
                </div>
              )}
            </div>
          </Card>
        ))}

        <Card className="p-6">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">Brand Assets</div>
          <div className="mt-5 grid gap-6 sm:grid-cols-2">
            <div>
              <Label className="mb-2 block">Company Logo (PNG/JPG)</Label>
              <FileUploader kind="logo" accept="image/*" value={s.logo_path || ""} onChange={(p) => setFile("logo_path", p)} label="Upload logo" />
            </div>
            <div>
              <Label className="mb-2 block">Signature Image (transparent PNG preferred)</Label>
              <FileUploader kind="signature" accept="image/*" value={s.signature_path || ""} onChange={(p) => setFile("signature_path", p)} label="Upload signature" />
            </div>
          </div>
        </Card>

        <Card className="border-teal-200 bg-teal-50 p-6">
          <div className="text-sm font-semibold text-teal-900">Enable Alerts</div>
          <p className="mt-2 text-sm text-teal-800">To turn on real-time enquiry alerts, add these keys to <code className="rounded bg-white px-1.5 py-0.5">/app/backend/.env</code> and restart the backend:</p>
          <ul className="mt-3 space-y-1 text-xs text-teal-900">
            <li>• <code>SENDGRID_API_KEY</code> — enquiry emails via SendGrid</li>
            <li>• <code>SENDGRID_FROM_EMAIL</code> — verified sender email</li>
            <li>• <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_WHATSAPP_FROM</code> — WhatsApp alerts</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
